import { Close as CloseIcon, FileTextIcon, UploadIcon } from 'components/icons';
import { Controller, FieldValues, SetFieldValue, useWatch } from 'react-hook-form';
import { t } from 'utils/getTranslations';
import { CommonInputProps } from './InputTypes';
import InputWrapper from './InputWrapper';
import { useMemoizedRules } from './useMemoizedRules';

interface Props<T extends FieldValues> extends CommonInputProps<T> {
    suffixComponent?: React.ReactNode;
    multiline?: boolean;
    setValue: SetFieldValue<T>;
    accept?: string;
    generatedAttachments?: string[];
    attachmentDisplayNames?: Record<string, string>;
    removeGeneratedAttachment?: (deletedAttachment: string) => void;
    maxFiles?: number;
}

// Drawn 12px, its target grown by `hit-area`.
const REMOVE_BUTTON_CLASS =
    'bg-grey-500 text-grey-0 hit-area relative flex size-3 cursor-pointer items-center justify-center rounded-full text-[10px] disabled:cursor-default disabled:opacity-50 new:size-4 new:bg-transparent new:text-muted-foreground new:hover:text-foreground';

// As tall as its button's area, so wrapped rows' areas do not cover each other. The new design draws an outline badge.
const CHIP_CLASS =
    'text-link flex min-h-8 items-center gap-0.5 text-sm pointer-coarse:min-h-11 new:min-h-5 new:h-5 new:gap-1 new:rounded-4xl new:border new:border-border new:ps-1.5 new:pe-0.5 new:text-xs new:font-medium new:text-foreground new:whitespace-nowrap';

/** The new design's file glyph at the head of a badge. */
const chipGlyph = <FileTextIcon aria-hidden className="new:block hidden size-3 shrink-0" />;

// TODO: Improve this component once backend supports email input
/**
 * @param generatedAttachments - displays static text styled as attachment
 */
const FileInput = <T extends FieldValues>({
    name,
    label,
    disabled,
    required,
    validate,
    control,
    setValue,
    generatedAttachments,
    attachmentDisplayNames,
    removeGeneratedAttachment,
    accept = '*',
    maxFiles,
}: Props<T>) => {
    // Keys built on the FE keep the raw invoice number, which may contain "/" (e.g. "invoice-2024/001.pdf"),
    // while the BE selection keys in attachmentDisplayNames are "/"-to-"-" normalized — hence the fallback lookup.
    const resolveDisplayName = (key: string) =>
        attachmentDisplayNames?.[key] ?? attachmentDisplayNames?.[key.split('/').join('-')] ?? key;
    const files = useWatch({ control, name });
    const rules = useMemoizedRules({ required, validate });

    const addFile = (event: React.ChangeEvent<HTMLInputElement>) => {
        const values = (files ?? []) as File[];
        const fileNames = values.map((val) => val.name);
        const eventFiles = Array.from(event.target.files ?? []);
        const [eventFile] = eventFiles;
        if (!fileNames.includes(eventFile.name)) {
            values.push(eventFile);
        }
        setValue(name, values);
        event.target.files = new DataTransfer().files;
    };

    const removeFile = (deletedFileName: string) => {
        const values = (files ?? ([] as File[])).filter((val) => val.name !== deletedFileName);
        setValue(name, values);
    };

    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field: { ref }, fieldState: { error } }) => {
                const isEmpty = !files || (Array.isArray(files) && files.length === 0);
                return (
                    <InputWrapper
                        name={name}
                        label={label}
                        required={required}
                        isEmpty={isEmpty}
                        hasErrors={!!error}
                        error={error}
                        className="border-divider new:border-b-0 new:py-0 border-b py-2"
                    >
                        {/* The new design lays the badges and Upload on one wrapping row. */}
                        <div className="bg-paper new:min-h-9 new:flex-row new:flex-wrap new:items-center new:gap-1.5 flex flex-col gap-1">
                            <div className="new:contents flex flex-wrap gap-1">
                                {(generatedAttachments ?? [])?.map((val: string) => (
                                    <div key={val} className={CHIP_CLASS}>
                                        {chipGlyph}
                                        {resolveDisplayName(val)}
                                        <button
                                            type="button"
                                            aria-label={`${t('remove')} ${resolveDisplayName(val)}`}
                                            className={REMOVE_BUTTON_CLASS}
                                            disabled={disabled}
                                            onClick={() => {
                                                if (removeGeneratedAttachment) {
                                                    removeGeneratedAttachment(val);
                                                }
                                            }}
                                        >
                                            <CloseIcon fontSize="inherit" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <div className="new:contents flex flex-wrap gap-1">
                                {(files ?? [])?.map((val: File) => (
                                    <div key={`${val.name}-${val.lastModified}`} className={CHIP_CLASS}>
                                        {chipGlyph}
                                        {val.name}
                                        <button
                                            type="button"
                                            aria-label={`${t('remove')} ${val.name}`}
                                            className={REMOVE_BUTTON_CLASS}
                                            disabled={disabled}
                                            onClick={() => removeFile(val.name)}
                                        >
                                            <CloseIcon fontSize="inherit" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                            {(!maxFiles || (files ?? []).length < maxFiles) && (
                                // The file input is the focusable control: `sr-only` keeps it off the screen
                                // but in the tab order, so Space/Enter on it opens the picker natively. A `hidden`
                                // input would make the label the only affordance, and a label takes no focus.
                                <label
                                    className="border-divider has-[:focus-visible]:outline-primary new:inline-flex new:h-6 new:items-center new:gap-1 new:rounded-md new:border-border new:bg-background new:font-medium new:hover:bg-muted w-fit cursor-pointer rounded-sm border px-2.5 py-0.5 text-xs font-semibold has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 aria-disabled:pointer-events-none aria-disabled:opacity-50"
                                    aria-disabled={disabled}
                                >
                                    <UploadIcon aria-hidden className="new:block hidden size-3.5" />
                                    {t('upload')}
                                    <input
                                        onChange={(e) => {
                                            addFile(e);
                                        }}
                                        disabled={disabled}
                                        ref={ref}
                                        type="file"
                                        accept={accept}
                                        className="sr-only"
                                        multiple
                                    />
                                </label>
                            )}
                        </div>
                    </InputWrapper>
                );
            }}
        />
    );
};

export default FileInput;
